import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import "moment/locale/fr";
import "moment/locale/de";
import "moment/locale/es";
import EditCalendarOutlinedIcon from "@mui/icons-material/EditCalendarOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SensorsIcon from "@mui/icons-material/Sensors";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import { resolveClubLogoUrl } from "../utils/resolveClubImageUrl";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
} from "@mui/material";
import { useBrand } from "../context/BrandContext";
import { grpplayerlist } from "./funtionsBD/Mainservice";
import { shouldUseContinueControlLabel } from "../utils/coachExerciseNavigation";
import { formatTrainingSessionHash } from "../utils/formatTrainingSessionLabel";
import "../pages/Exerciseadd/Exercise.css";
import "../components/KotCoachStartRound.css";

/**
 * Shared card showing the current training session: group, session label, date and time.
 * Used on both Training Plan (Planning) and Training Room (Training) for a coherent presentation.
 * Pass variant="planning" for the mobile-first planning layout.
 */
export function SessionInfoCard({
  selectedSession,
  selectedgrp,
  allowEditSessionMeta = false,
  onApplySessionMeta,
  /** Planning page: exercise list has local duration edits not yet saved */
  planningExerciseDurationsDirty = false,
  /** Planning page: sum of all exercise durations currently displayed */
  planningExerciseTotalMinutes = null,
  /** Planning page: persist all modified exercise/sequence durations (after session duration) */
  onApplyPlanningExerciseDurations,
  /** Opens live exercise control (Planning / Training) — rendered top-right on the card */
  onExerciseControlStart,
  /** Opens full-screen live training view (Training room) */
  onLiveTrainingStart,
  /**
   * Planning: true/false to show "Continue" vs "Start". When omitted (e.g. Training room),
   * label is inferred from session state and exercise rows (finalized/started/etc.).
   */
  exerciseControlUseContinue,
  /** Training room: exercises from TableSetup so "Continue" shows if any exercise already progressed. */
  sessionExercisesSnapshot,
  /** Planning / session card: KoT start-round control (top-right, next to Start/Continue). */
  kotStartRoundSlot = null,
  /** "planning" = mobile-first plan layout; default keeps Training room presentation */
  variant = "default",
}) {
  const { t, i18n } = useTranslation();
  const { brand } = useBrand();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [scheduleDraft, setScheduleDraft] = useState({
    date: "",
    time: "",
  });
  const [sessionDurationDraft, setSessionDurationDraft] = useState("");
  const [metaSaving, setMetaSaving] = useState(false);
  const [metaError, setMetaError] = useState(null);
  const [durationError, setDurationError] = useState(null);
  const [registeredPlayerCount, setRegisteredPlayerCount] = useState(null);
  const [registeredPlayersLoading, setRegisteredPlayersLoading] =
    useState(false);

  const getTimeInputValue = (timeRaw) => {
    if (!timeRaw) return "";
    const value = String(timeRaw).trim();
    if (/^\d{2}:\d{2}/.test(value)) return value.slice(0, 5);
    const parsed = moment(value, ["h:mm A", "hh:mm A", "H:mm", "HH:mm"], true);
    return parsed.isValid() ? parsed.format("HH:mm") : "";
  };

  const normalizeLocaleCode = (lng) => {
    const base = (lng || "en").split("-")[0].toLowerCase();
    if (base === "gr") return "de";
    if (base === "sp") return "es";
    return base;
  };
  const localeCode = normalizeLocaleCode(i18n.language);

  useEffect(() => {
    setScheduleDraft({
      date: selectedSession?.ts_date
        ? moment(selectedSession.ts_date).format("YYYY-MM-DD")
        : "",
      time: getTimeInputValue(selectedSession?.ts_start_time),
    });
    setSessionDurationDraft(
      selectedSession?.ts_duration != null && selectedSession.ts_duration !== ""
        ? String(selectedSession.ts_duration)
        : ""
    );
    setMetaError(null);
    setDurationError(null);
    setEditDialogOpen(false);
  }, [selectedSession?.ts_id, selectedSession?.ts_date, selectedSession?.ts_start_time, selectedSession?.ts_duration]);

  useEffect(() => {
    if (!selectedSession?.ts_id) {
      setRegisteredPlayerCount(null);
      return;
    }
    const grId =
      selectedgrp?.groupDetails?.gr_id ??
      selectedgrp?.gr_id ??
      selectedSession.gr_id ??
      selectedSession._groupId ??
      selectedSession?.groupDetails?.gr_id ??
      null;
    if (!grId) {
      setRegisteredPlayerCount(null);
      return;
    }

    let cancelled = false;
    const fetchRegisteredCount = async () => {
      setRegisteredPlayersLoading(true);
      try {
        const response = await grpplayerlist({
          ts_id: selectedSession.ts_id,
          gr_id: grId,
        });
        if (cancelled) return;
        const list = response.data?.data || [];
        const count = list.filter((p) => p.presensee === true).length;
        setRegisteredPlayerCount(count);
      } catch {
        if (!cancelled) setRegisteredPlayerCount(null);
      } finally {
        if (!cancelled) setRegisteredPlayersLoading(false);
      }
    };

    fetchRegisteredCount();

    const onVisible = () => {
      if (document.visibilityState === "visible") fetchRegisteredCount();
    };
    document.addEventListener("visibilitychange", onVisible);
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") fetchRegisteredCount();
    }, 20000);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(interval);
    };
  }, [
    selectedSession?.ts_id,
    selectedSession?.gr_id,
    selectedSession?._groupId,
    selectedSession?.groupDetails?.gr_id,
    selectedgrp?.groupDetails?.gr_id,
    selectedgrp?.gr_id,
  ]);

  if (!selectedSession?.ts_id) return null;

  const groupName =
    selectedgrp?.groupDetails?.gr_name ||
    selectedgrp?.gr_name ||
    selectedSession._coachHomeGroupName ||
    selectedSession.group_name ||
    selectedSession.gr_name ||
    selectedSession?.groupDetails?.gr_name ||
    "";
  const sessionHash = formatTrainingSessionHash(selectedSession);
  const sessionLabel = sessionHash
    ? `${t("session")} #${sessionHash}`
    : selectedSession.name || selectedSession.ts_id?.substring(0, 8) || "";
  const clubLogoUrl = resolveClubLogoUrl(
    selectedgrp?.groupDetails?.club_image ||
      selectedgrp?.groupDetails?.gr_image ||
      selectedSession?.club_image ||
      selectedSession?.club_details?.club_image,
    brand
  );
  const dateStr = selectedSession.ts_date
    ? moment(selectedSession.ts_date).locale(localeCode).format("dddd D MMMM YYYY")
    : null;
  const formatDisplayTime = (timeRaw) => {
    if (!timeRaw) return null;
    const parsed = moment(timeRaw, ["HH:mm:ss", "HH:mm", "h:mm A"], true);
    if (parsed.isValid()) return parsed.locale(localeCode).format("LT");
    return String(timeRaw).slice(0, 5);
  };
  const timeStr = selectedSession.ts_start_time
    ? formatDisplayTime(selectedSession.ts_start_time)
    : null;
  const endTimeStr = selectedSession.ts_end_time
    ? formatDisplayTime(selectedSession.ts_end_time)
    : null;
  const normalizedSessionDurationCurrent = String(
    selectedSession?.ts_duration != null ? selectedSession.ts_duration : ""
  ).trim();
  const normalizedSessionDurationDraft = String(sessionDurationDraft ?? "").trim();
  const sessionDurationDirty =
    allowEditSessionMeta &&
    !!onApplySessionMeta &&
    normalizedSessionDurationDraft !== normalizedSessionDurationCurrent;
  const hasDurationChangesToApply =
    sessionDurationDirty ||
    (planningExerciseDurationsDirty && !!onApplyPlanningExerciseDurations);
  const sessionDurationForComparison = parseInt(
    String(
      allowEditSessionMeta && onApplySessionMeta
        ? normalizedSessionDurationDraft
        : normalizedSessionDurationCurrent
    ),
    10
  );
  const hasComparableDurations =
    Number.isFinite(sessionDurationForComparison) &&
    sessionDurationForComparison > 0 &&
    planningExerciseTotalMinutes != null;
  const durationGapMinutes = hasComparableDurations
    ? sessionDurationForComparison - planningExerciseTotalMinutes
    : 0;
  const hasExerciseDurationMismatch = hasComparableDurations && durationGapMinutes !== 0;

  const useContinueLabel =
    typeof exerciseControlUseContinue === "boolean"
      ? exerciseControlUseContinue
      : shouldUseContinueControlLabel(
          selectedSession,
          Array.isArray(sessionExercisesSnapshot) ? sessionExercisesSnapshot : [],
          {}
        );

  const blockStartForNoPlayers =
    !!onExerciseControlStart &&
    !useContinueLabel &&
    (registeredPlayersLoading ||
      registeredPlayerCount === null ||
      registeredPlayerCount < 1);

  const exerciseControlButtonTitle = () => {
    if (blockStartForNoPlayers) {
      return (
        t("startRequiresRegisteredPlayers") ||
        "Add at least one registered player to start."
      );
    }
    return t("exerciseControl") || "Exercise control";
  };

  const handleApplyDurationChanges = async () => {
    setDurationError(null);
    const parsedDuration = parseInt(normalizedSessionDurationDraft, 10);
    if (sessionDurationDirty && (Number.isNaN(parsedDuration) || parsedDuration < 1)) {
      setDurationError(t("durationRequired") || "Duration is required");
      return;
    }
    try {
      setMetaSaving(true);
      if (sessionDurationDirty) {
        const currentDate = selectedSession?.ts_date
          ? moment(selectedSession.ts_date).format("YYYY-MM-DD")
          : "";
        const currentTime = getTimeInputValue(selectedSession?.ts_start_time);
        if (!currentDate || !currentTime) {
          setDurationError(t("errorSaving") || "Could not save");
          return;
        }
        await onApplySessionMeta({
          date: currentDate,
          time: currentTime,
          duration: parsedDuration,
        });
      }
      if (planningExerciseDurationsDirty && onApplyPlanningExerciseDurations) {
        await onApplyPlanningExerciseDurations();
      }
    } catch (err) {
      const api =
        err && typeof err === "object" ? err : { message: String(err ?? "") };
      const br = api?.data;
      let msg = api?.message || t("errorSaving") || "Could not save";
      if (
        br &&
        typeof br.total_minutes === "number" &&
        typeof br.ts_duration === "number"
      ) {
        const over = br.total_minutes - br.ts_duration;
        msg = `${msg} — ${t("session")}: ${br.ts_duration} ${t("min")} · ${
          t("plannedExercisesTotal") || "Exercises total"
        }: ${br.total_minutes} ${t("min")}${
          over > 0
            ? ` (${over} ${t("min")} ${t("overAllocated") || "over"})`
            : ""
        }`;
      }
      setDurationError(msg);
    } finally {
      setMetaSaving(false);
    }
  };

  const startControlDisabled =
    hasDurationChangesToApply ||
    hasExerciseDurationMismatch ||
    metaSaving ||
    blockStartForNoPlayers;

  const meterRatio =
    hasComparableDurations && sessionDurationForComparison > 0
      ? Math.min(
          1.25,
          planningExerciseTotalMinutes / sessionDurationForComparison
        )
      : 0;
  const meterPct = Math.min(100, Math.round(meterRatio * 100));
  const meterFillClass =
    durationGapMinutes < 0
      ? "planning-session-card__meter-fill--over"
      : durationGapMinutes === 0
        ? "planning-session-card__meter-fill--match"
        : "";

  const dateChip = dateStr
    ? moment(selectedSession.ts_date).locale(localeCode).format("ddd D MMM")
    : null;
  const timeChip =
    timeStr || endTimeStr
      ? `${timeStr || ""}${
          endTimeStr && timeStr !== endTimeStr ? ` – ${endTimeStr}` : ""
        }`
      : null;
  const playersChip =
    registeredPlayersLoading && registeredPlayerCount === null
      ? "…"
      : registeredPlayerCount ?? "—";

  const scheduleEditDialog =
    allowEditSessionMeta && onApplySessionMeta && selectedSession.ts_id ? (
      <Dialog
        open={editDialogOpen}
        onClose={() => !metaSaving && setEditDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          className: "planning-schedule-dialog",
          sx: {
            backgroundColor: "var(--surface-color)",
            color: "var(--text-color)",
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle sx={{ color: "var(--text-color)" }}>
          {t("editsession") || "Edit session"}
        </DialogTitle>
        <DialogContent>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              marginTop: "8px",
            }}
          >
            <label
              style={{ display: "flex", flexDirection: "column", gap: "6px" }}
            >
              <span style={{ color: "var(--text-secondary)", fontSize: "13px" }}>
                {t("date") || "Date"}
              </span>
              <input
                type="date"
                value={scheduleDraft.date}
                onChange={(e) =>
                  setScheduleDraft((prev) => ({
                    ...prev,
                    date: e.target.value,
                  }))
                }
                disabled={metaSaving}
                className="planning-schedule-dialog__input"
              />
            </label>
            <label
              style={{ display: "flex", flexDirection: "column", gap: "6px" }}
            >
              <span style={{ color: "var(--text-secondary)", fontSize: "13px" }}>
                {t("starttime") || "Start time"}
              </span>
              <input
                type="time"
                value={scheduleDraft.time}
                onChange={(e) =>
                  setScheduleDraft((prev) => ({
                    ...prev,
                    time: e.target.value,
                  }))
                }
                disabled={metaSaving}
                className="planning-schedule-dialog__input"
              />
            </label>
          </div>
          {metaError && (
            <div
              style={{
                color: "var(--text-color)",
                fontSize: "13px",
                marginTop: "10px",
                opacity: 0.9,
              }}
              className="planning-schedule-dialog__error"
            >
              {metaError}
            </div>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            disabled={metaSaving}
            onClick={() => setEditDialogOpen(false)}
            sx={{ color: "var(--text-secondary)" }}
          >
            {t("cancel") || "Cancel"}
          </Button>
          <Button
            variant="contained"
            disabled={metaSaving}
            onClick={async () => {
              setMetaError(null);
              if (!scheduleDraft.date || !scheduleDraft.time) {
                setMetaError(
                  t("durationRequired") || "Enter a valid date and time."
                );
                return;
              }
              const sessionDuration = parseInt(
                String(selectedSession?.ts_duration ?? "").trim(),
                10
              );
              if (Number.isNaN(sessionDuration) || sessionDuration < 1) {
                setMetaError(t("durationRequired") || "Duration is required");
                return;
              }
              try {
                setMetaSaving(true);
                await onApplySessionMeta({
                  date: scheduleDraft.date,
                  time: scheduleDraft.time,
                  duration: sessionDuration,
                });
                setEditDialogOpen(false);
              } catch (err) {
                setMetaError(
                  err?.message || t("errorSaving") || "Could not save"
                );
              } finally {
                setMetaSaving(false);
              }
            }}
            sx={{
              backgroundColor: brand?.color_primary || "#0877BC",
              color: "#fff",
              "&:hover": {
                backgroundColor: brand?.color_primary || "#0877BC",
              },
            }}
          >
            {t("apply") || "Apply"}
          </Button>
        </DialogActions>
      </Dialog>
    ) : null;

  if (variant === "planning") {
    return (
      <section
        className="planning-session-card"
        aria-label={t("planning") || "Planning"}
      >
        <div
          className="planning-session-card__accent"
          style={{
            background:
              brand?.color_primary || "var(--brand-primary, #0877BC)",
          }}
        />
        <div className="planning-session-card__body">
          <div className="planning-session-card__identity">
            <div className="planning-session-card__brand">
              {clubLogoUrl ? (
                <img
                  className="planning-session-card__logo"
                  src={clubLogoUrl}
                  alt=""
                />
              ) : (
                <span className="planning-session-card__logo planning-session-card__logo--fallback" aria-hidden>
                  {(groupName || "C").slice(0, 1).toUpperCase()}
                </span>
              )}
              <div className="planning-session-card__titles">
                <h2 className="planning-session-card__group">{groupName || "—"}</h2>
                {sessionLabel ? (
                  <p className="planning-session-card__session">{sessionLabel}</p>
                ) : null}
              </div>
            </div>
            {allowEditSessionMeta && onApplySessionMeta && selectedSession.ts_id ? (
              <button
                type="button"
                className="planning-session-card__edit"
                onClick={() => setEditDialogOpen(true)}
                title={t("editSchedule") || "Edit schedule"}
                aria-label={t("editSchedule") || "Edit schedule"}
              >
                <EditCalendarOutlinedIcon style={{ fontSize: 20 }} />
              </button>
            ) : null}
          </div>

          <div className="planning-session-card__chips">
            {dateChip ? (
              <span className="planning-session-card__chip">
                <EventOutlinedIcon />
                <span className="planning-session-card__chip-value">
                  {dateChip}
                </span>
              </span>
            ) : null}
            {timeChip ? (
              <span className="planning-session-card__chip">
                <ScheduleOutlinedIcon />
                <span className="planning-session-card__chip-value">
                  {timeChip}
                </span>
              </span>
            ) : null}
            {allowEditSessionMeta && onApplySessionMeta ? (
              <span className="planning-session-card__chip planning-session-card__chip--duration">
                <TimerOutlinedIcon />
                <span className="planning-session-card__duration-input-wrap">
                  <input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    step={1}
                    value={sessionDurationDraft}
                    onChange={(e) => {
                      setSessionDurationDraft(e.target.value);
                      setDurationError(null);
                    }}
                    disabled={metaSaving}
                    className="ex_add_box__duration-input ex_add_box__duration-input--editing"
                    aria-label={t("sessionDuration") || "Session duration"}
                  />
                  <span className="planning-session-card__chip-value">
                    {t("min")}
                  </span>
                </span>
              </span>
            ) : selectedSession.ts_duration != null &&
              selectedSession.ts_duration !== "" ? (
              <span className="planning-session-card__chip">
                <TimerOutlinedIcon />
                <span className="planning-session-card__chip-value">
                  {selectedSession.ts_duration} {t("min")}
                </span>
              </span>
            ) : null}
            <span className="planning-session-card__chip">
              <GroupOutlinedIcon />
              <span className="planning-session-card__chip-value">
                {playersChip}
              </span>
            </span>
          </div>

          {hasComparableDurations ? (
            <div className="planning-session-card__meter">
              <div
                className="planning-session-card__meter-track"
                role="progressbar"
                aria-valuenow={planningExerciseTotalMinutes}
                aria-valuemin={0}
                aria-valuemax={sessionDurationForComparison}
              >
                <div
                  className={`planning-session-card__meter-fill ${meterFillClass}`}
                  style={{ width: `${meterPct}%` }}
                />
              </div>
              <div className="planning-session-card__meter-stats">
                <span>
                  <strong>{planningExerciseTotalMinutes}</strong> /{" "}
                  <strong>{sessionDurationForComparison}</strong> {t("min")}
                </span>
                {durationGapMinutes > 0 ? (
                  <span className="planning-session-card__meter-hint">
                    {t("remainingMinutesToAdd", { count: durationGapMinutes }) ||
                      `${durationGapMinutes} min left`}
                  </span>
                ) : null}
                {durationGapMinutes < 0 ? (
                  <span className="planning-session-card__meter-hint planning-session-card__meter-hint--warn">
                    {t("extraMinutesToRemove", {
                      count: Math.abs(durationGapMinutes),
                    }) || `${Math.abs(durationGapMinutes)} min over`}
                  </span>
                ) : null}
                {durationGapMinutes === 0 ? (
                  <span className="planning-session-card__meter-hint planning-session-card__meter-hint--ok">
                    {t("durationsMatch") || "Matches session"}
                  </span>
                ) : null}
              </div>
            </div>
          ) : null}

          {durationError ? (
            <p className="planning-session-card__alert planning-session-card__alert--error">
              {durationError}
            </p>
          ) : null}
          {hasExerciseDurationMismatch ? (
            <p className="planning-session-card__alert planning-session-card__alert--warn">
              {durationGapMinutes < 0
                ? t("exerciseDurationOverWarning") ||
                  "Planned exercises exceed the session duration. You can still add or edit exercises; match the totals before starting live control."
                : t("exerciseDurationMismatchWarning") ||
                  "Adjust exercise or session duration so the totals match before starting live control."}
            </p>
          ) : null}
          {planningExerciseDurationsDirty && onApplyPlanningExerciseDurations ? (
            <p className="planning-session-card__alert planning-session-card__alert--hint">
              {t("unsavedExerciseDurationsHint") ||
                "Exercise duration edits are not saved until you apply changes."}
            </p>
          ) : null}
        </div>

        <div className="planning-session-card__actions">
          {hasDurationChangesToApply ? (
            <button
              type="button"
              className="planning-session-card__btn planning-session-card__btn--apply"
              onClick={handleApplyDurationChanges}
              disabled={metaSaving}
            >
              {t("applyChanges") || "Apply changes"}
            </button>
          ) : null}
          <div className="planning-session-card__action-row">
            {onExerciseControlStart ? (
              <button
                type="button"
                className="planning-session-card__btn planning-session-card__btn--primary"
                onClick={onExerciseControlStart}
                disabled={startControlDisabled}
                title={exerciseControlButtonTitle()}
              >
                <PlayArrowIcon style={{ fontSize: 22 }} />
                {useContinueLabel
                  ? t("continueControl") || "Continue"
                  : t("startControl") || "Start"}
              </button>
            ) : null}
            {kotStartRoundSlot}
          </div>
        </div>
        {scheduleEditDialog}
      </section>
    );
  }

  return (
    <div
      className="session-info-card"
      style={{
        marginBottom: "16px",
        padding: "14px 18px",
        backgroundColor: "var(--surface-color)",
        borderRadius: "10px",
        boxShadow: "0 4px 15px var(--shadow-color)",
        borderLeft: `4px solid ${brand?.color_primary || "var(--brand-primary, #0877BC)"}`,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          flexWrap: "wrap",
          marginBottom: "10px",
        }}
      >
        <div
          className="session-info-card__title"
          style={{
            fontWeight: "600",
            fontSize: "16px",
            color: "var(--text-color)",
            flex: "1 1 200px",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: "6px",
          }}
        >
          <span>{groupName}</span>
          <span
            className="session-info-card__meta"
            style={{
              fontWeight: 500,
              fontSize: "13px",
              color: "var(--text-secondary)",
            }}
          >
            <span className="session-info-card__meta-label" style={{ color: "var(--text-secondary)", marginRight: "6px" }}>
              {t("registeredPlayersLabel") || "Registered players"}:
            </span>
            {registeredPlayersLoading && registeredPlayerCount === null
              ? "…"
              : registeredPlayerCount ?? "—"}
          </span>
        </div>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", justifyContent: "flex-end", alignItems: "center" }}>
          {allowEditSessionMeta && onApplySessionMeta && selectedSession.ts_id && (
            <button
              type="button"
              className="session-info-card__edit-btn"
              onClick={() => setEditDialogOpen(true)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                flexShrink: 0,
                padding: "10px 14px",
                minHeight: "42px",
                borderRadius: "10px",
                border: `1px solid ${brand?.color_primary || "#0877BC"}`,
                cursor: "pointer",
                fontWeight: 600,
                fontSize: "14px",
                color: brand?.color_primary || "#0877BC",
                backgroundColor: "var(--surface-color)",
              }}
              title={t("editsession") || "Edit session"}
            >
              <EditCalendarOutlinedIcon style={{ fontSize: 20 }} />
              {t("editSchedule") || "Edit schedule"}
            </button>
          )}
          {hasDurationChangesToApply && (
            <button
              type="button"
              onClick={handleApplyDurationChanges}
              disabled={metaSaving}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                flexShrink: 0,
                padding: "10px 16px",
                minHeight: "42px",
                borderRadius: "10px",
                border: "none",
                cursor: metaSaving ? "not-allowed" : "pointer",
                fontWeight: 700,
                fontSize: "14px",
                color: "#fff",
                opacity: metaSaving ? 0.75 : 1,
                backgroundColor: brand?.color_primary || "#0877BC",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              }}
              title={t("applyChanges") || "Apply changes"}
            >
              {t("applyChanges") || "Apply changes"}
            </button>
          )}
          {onLiveTrainingStart && (
            <button
              type="button"
              className="session-info-card__live-btn"
              data-tour-id="live-training-open"
              onClick={onLiveTrainingStart}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                flexShrink: 0,
                padding: "10px 16px",
                minHeight: "42px",
                borderRadius: "10px",
                border: "none",
                cursor: "pointer",
                fontWeight: 700,
                fontSize: "15px",
                color: "#fff",
                background: `linear-gradient(135deg, ${
                  brand?.color_primary_light || "#0a8cd8"
                }, ${brand?.color_primary || "#0877BC"})`,
                boxShadow: `0 4px 12px rgba(${
                  brand?.color_primary_rgb || "8, 119, 188"
                }, 0.35)`,
              }}
              title={t("liveTrainingTitle") || "Live Training"}
            >
              <SensorsIcon style={{ fontSize: 22 }} />
              {t("liveTrainingTitle") || "Live Training"}
            </button>
          )}
          {onExerciseControlStart && (
            <button
              type="button"
              onClick={onExerciseControlStart}
              disabled={startControlDisabled}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                flexShrink: 0,
                padding: "12px 24px",
                minHeight: "44px",
                borderRadius: "22px",
                border: "none",
                cursor: startControlDisabled ? "not-allowed" : "pointer",
                fontWeight: 600,
                fontSize: "15px",
                letterSpacing: "-0.15px",
                color: "#fff",
                background: startControlDisabled
                  ? brand?.color_primary || "#0877BC"
                  : `linear-gradient(135deg, ${brand?.color_primary || "#0877BC"} 0%, ${brand?.color_primary_dark || "#065a8f"} 100%)`,
                boxShadow: startControlDisabled
                  ? "none"
                  : `0 6px 18px rgba(${
                      brand?.color_primary_rgb || "8, 119, 188"
                    }, 0.35)`,
                opacity: startControlDisabled ? 0.6 : 1,
              }}
              className="ios-start-btn"
              title={exerciseControlButtonTitle()}
            >
              <PlayArrowIcon style={{ fontSize: 22 }} />
              {useContinueLabel
                ? t("continueControl") || "Continue"
                : t("startControl") || "Start"}
            </button>
          )}
          {kotStartRoundSlot}
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "16px 24px",
          alignItems: "baseline",
          fontSize: "14px",
        }}
      >
        <span style={{ color: brand?.color_primary || "var(--brand-primary, #0877BC)", fontWeight: "600" }}>{sessionLabel}</span>
        {dateStr && (
          <span className="session-info-card__schedule-line" style={{ color: "var(--text-secondary)" }}>
            <span className="session-info-card__meta-label" style={{ color: "var(--text-secondary)", marginRight: "4px" }}>{t("date")}:</span>
            <span className="session-info-card__schedule-value">{dateStr}</span>
          </span>
        )}
        {(timeStr || endTimeStr) && (
          <span className="session-info-card__schedule-line" style={{ color: "var(--text-secondary)" }}>
            <span className="session-info-card__meta-label" style={{ color: "var(--text-secondary)", marginRight: "4px" }}>{t("time")}:</span>
            <span className="session-info-card__schedule-value">
              {timeStr}
              {endTimeStr && timeStr !== endTimeStr ? ` – ${endTimeStr}` : ""}
            </span>
          </span>
        )}
        {allowEditSessionMeta && onApplySessionMeta ? (
          <span
            style={{
              color: "var(--text-secondary)",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <span className="session-info-card__meta-label" style={{ color: "var(--text-secondary)", marginRight: "2px" }}>
              {t("sessionDuration") || "Session duration"}:
            </span>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              value={sessionDurationDraft}
              onChange={(e) => {
                setSessionDurationDraft(e.target.value);
                setDurationError(null);
              }}
              disabled={metaSaving}
              className="ex_add_box__duration-input ex_add_box__duration-input--editing"
            />
            <span>{t("min")}</span>
          </span>
        ) : selectedSession.ts_duration != null && selectedSession.ts_duration !== "" ? (
          <span className="session-info-card__schedule-line" style={{ color: "var(--text-secondary)" }}>
            <span className="session-info-card__meta-label" style={{ color: "var(--text-secondary)", marginRight: "4px" }}>{t("sessionDuration") || "Session duration"}:</span>
            <span className="session-info-card__schedule-value">{selectedSession.ts_duration} {t("min")}</span>
          </span>
        ) : null}
      </div>
      {durationError && (
        <div style={{ marginTop: "8px", color: "#c62828", fontSize: "13px" }}>
          {durationError}
        </div>
      )}
      {hasComparableDurations && (
        <div
          style={{
            marginTop: "10px",
            fontSize: "13px",
            color: hasExerciseDurationMismatch ? "#ff9f0a" : "var(--text-secondary)",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "6px 10px",
          }}
        >
          <span>
            <span className="session-info-card__meta-label" style={{ color: "var(--text-secondary)", marginRight: "4px" }}>
              {t("plannedExercisesTotal") || "Exercises total"}:
            </span>
            <strong style={{ color: "var(--text-color)" }}>{planningExerciseTotalMinutes}</strong> {t("min")}
          </span>
          <span style={{ color: "var(--border-color)" }} aria-hidden>
            |
          </span>
          <span>
            <span className="session-info-card__meta-label" style={{ color: "var(--text-secondary)", marginRight: "4px" }}>
              {t("sessionDuration") || "Session"}:
            </span>
            <strong style={{ color: "var(--text-color)" }}>{sessionDurationForComparison}</strong> {t("min")}
          </span>
          {durationGapMinutes > 0 && (
            <span style={{ fontWeight: 600 }}>
              ·{" "}
              {t("remainingMinutesToAdd", { count: durationGapMinutes }) ||
                `${durationGapMinutes} min left to allocate`}
            </span>
          )}
          {durationGapMinutes < 0 && (
            <span style={{ fontWeight: 600 }}>
              ·{" "}
              {t("extraMinutesToRemove", {
                count: Math.abs(durationGapMinutes),
              }) ||
                `${Math.abs(durationGapMinutes)} min over session`}
            </span>
          )}
          {durationGapMinutes === 0 && (
            <span style={{ color: "#2e7d32", fontWeight: 600 }}>
              · {t("durationsMatch") || "Matches session"}
            </span>
          )}
        </div>
      )}
      {hasExerciseDurationMismatch && (
        <div
          style={{
            marginTop: "8px",
            color: "#b26a00",
            backgroundColor: "#fff8e1",
            border: "1px solid #ffe082",
            borderRadius: "8px",
            padding: "8px 12px",
            fontSize: "12px",
          }}
        >
          {durationGapMinutes < 0
            ? t("exerciseDurationOverWarning") ||
              "Planned exercises exceed the session duration. You can still add or edit exercises; match the totals before starting live control."
            : t("exerciseDurationMismatchWarning") ||
              "Adjust exercise or session duration so the totals match before starting live control."}
        </div>
      )}
      {planningExerciseDurationsDirty && onApplyPlanningExerciseDurations && (
        <div style={{ marginTop: "12px", color: "#666", fontSize: "13px" }}>
          {t("unsavedExerciseDurationsHint") ||
            "Exercise or sequence duration edits below are not saved until you use save in the edit dialog."}
        </div>
      )}
      {scheduleEditDialog}
    </div>
  );
}
